<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>管理员管理</h1>
        <p>按角色分层授权，账号只能管理自己被授权的学校</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" class="primary-btn" icon="el-icon-plus" @click="handleCreate">
          新增{{ creatableRoleLabel }}
        </el-button>
      </div>
    </div>

    <!-- ── 角色说明 ───────────────────────────────────────── -->
    <div class="role-bar">
      <div class="role-bar__item" v-for="item in roleLegend" :key="item.role">
        <span class="badge" :class="item.className"></span>
        <div class="role-bar__text">
          <span class="role-bar__name">{{ item.label }}</span>
          <span class="role-bar__desc">{{ item.desc }}</span>
        </div>
      </div>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="listQuery.username"
        placeholder="搜索用户名"
        clearable
        prefix-icon="el-icon-search"
        class="search-input"
        @keyup.enter.native="handleFilter"
        @clear="handleFilter"
      />
      <el-select v-model="listQuery.role" placeholder="全部角色" clearable class="role-select" @change="handleFilter">
        <el-option label="超级管理员" value="super" />
        <el-option label="管理员" value="admin" />
        <el-option label="运营" value="operator" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleFilter">查询</el-button>
      <el-button class="ghost-btn" @click="resetFilter">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 个</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="listLoading" :data="list" style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column label="账号" min-width="170">
          <template slot-scope="{ row }">
            <div class="cell-main">{{ row.nickname }}</div>
            <div class="cell-sub cell-mono">@{{ row.username }}</div>
          </template>
        </el-table-column>
        <el-table-column label="角色" width="120">
          <template slot-scope="{ row }">
            <span class="role-chip" :class="'role-chip--' + row.role">{{ row.roleLabel }}</span>
          </template>
        </el-table-column>
        <el-table-column label="管辖学校" min-width="260">
          <template slot-scope="{ row }">
            <span v-if="row.superAdmin" class="scope-all">全部学校（不受限制）</span>
            <span v-else-if="!row.schoolNames || !row.schoolNames.length" class="scope-none">
              未授权任何学校
            </span>
            <template v-else>
              <span v-for="name in row.schoolNames.slice(0, 3)" :key="name" class="school-chip">{{ name }}</span>
              <el-tooltip v-if="row.schoolNames.length > 3" :content="row.schoolNames.join('、')">
                <span class="school-more">+{{ row.schoolNames.length - 3 }}</span>
              </el-tooltip>
            </template>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="110">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="row.status === 1 ? 's1' : 's2'">
              <i />{{ row.status === 1 ? '正常' : '禁用' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="创建人" width="120">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ row.parentNickname || '—' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ formatDate(row.createdAt) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button v-if="canEdit(row)" type="button" class="act act-plain" @click="handleUpdate(row)">编辑</button>
              <el-dropdown v-if="canResetPassword(row) || canDelete(row)" trigger="click" @command="cmd => handleAction(cmd, row)">
                <button type="button" class="act act-plain act-more">
                  更多<i class="el-icon-arrow-down" />
                </button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-if="canResetPassword(row)" command="reset">
                    <i class="el-icon-key" /> 重置密码
                  </el-dropdown-item>
                  <el-dropdown-item v-if="canDelete(row)" command="delete" divided>
                    <i class="el-icon-delete" /> 删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <span v-if="!canEdit(row) && !canResetPassword(row) && !canDelete(row)" class="no-action">仅可查看</span>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <el-pagination
          v-show="total > 0"
          background
          :current-page="listQuery.page"
          :page-size="listQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="560px">
      <el-form ref="form" :model="form" :rules="rules" label-width="100px">
        <el-form-item label="用户名" prop="username">
          <el-input v-model="form.username" :disabled="isEdit" placeholder="登录用的账号" />
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="form.nickname" />
        </el-form-item>
        <el-form-item v-if="!isEdit" label="初始密码" prop="password">
          <el-input v-model="form.password" type="password" show-password placeholder="至少 6 位" />
        </el-form-item>

        <el-form-item label="角色" prop="role">
          <el-radio-group v-model="form.role" :disabled="roleLocked" @change="handleRoleChange">
            <el-radio v-for="item in assignableRoles" :key="item.value" :label="item.value">
              {{ item.label }}
            </el-radio>
          </el-radio-group>
          <div class="field-hint">{{ roleHint }}</div>
        </el-form-item>

        <el-form-item label="管辖学校" prop="schoolIds">
          <el-select
            v-model="form.schoolIds"
            multiple
            filterable
            collapse-tags
            placeholder="选择该账号可以管理的学校"
            style="width: 100%;"
            :disabled="form.role === 'super'"
          >
            <el-option
              v-for="school in schoolOptions"
              :key="school.id"
              :label="school.name"
              :value="school.id"
            />
          </el-select>
          <div class="field-hint" v-if="form.role !== 'super'">
            不选学校，该账号登录后看不到任何数据。
            <el-button type="text" @click="selectAllSchools" v-if="schoolOptions.length">全选</el-button>
            <el-button type="text" @click="form.schoolIds = []" v-if="form.schoolIds.length">清空</el-button>
          </div>
          <div class="field-hint" v-else>超级管理员不受学校范围限制。</div>
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status" :disabled="isSelf">
            <el-radio :label="1">正常</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" class="primary-btn" :loading="submitting" @click="submitForm">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getAdminList, createAdmin, updateAdmin, deleteAdmin, resetAdminPassword } from '@/api/admin'
import { getSchoolOptions } from '@/api/school'

export default {
  name: 'Admin',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      submitting: false,
      listQuery: {
        page: 1,
        size: 10,
        username: '',
        role: ''
      },
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      isSelf: false,
      roleLocked: false,
      schoolOptions: [],
      form: {
        id: null,
        username: '',
        nickname: '',
        password: '',
        role: 'operator',
        schoolIds: [],
        status: 1
      },
      rules: {
        username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        nickname: [{ required: true, message: '请输入昵称', trigger: 'blur' }],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于 6 位', trigger: 'blur' }
        ],
        role: [{ required: true, message: '请选择角色', trigger: 'change' }]
      }
    }
  },
  computed: {
    ...mapGetters(['isSuperAdmin', 'adminInfo']),
    /** 我能创建哪些角色 —— 只能创建比自己低一级的 */
    assignableRoles() {
      if (this.isSuperAdmin) {
        return [
          { value: 'admin', label: '管理员' },
          { value: 'operator', label: '运营' }
        ]
      }
      return [{ value: 'operator', label: '运营' }]
    },
    creatableRoleLabel() {
      return this.isSuperAdmin ? '管理员 / 运营' : '运营'
    },
    roleHint() {
      if (this.form.role === 'super') {
        return '平台方，拥有全部菜单与全部学校的数据。'
      }
      if (this.form.role === 'admin') {
        return '只看得到被授权学校的数据，并可在这些范围内添加运营。'
      }
      return '只能处理被授权学校范围内的日常事务。'
    },
    roleLegend() {
      return [
        { role: 'super', label: '超级管理员', className: 'badge--super', desc: '全部菜单 · 全部数据' },
        { role: 'admin', label: '管理员', className: 'badge--admin', desc: '授权菜单 · 授权学校数据' },
        { role: 'operator', label: '运营', className: 'badge--operator', desc: '授权菜单 · 授权学校数据' }
      ]
    }
  },
  created() {
    this.fetchData()
    this.fetchSchoolOptions()
  },
  methods: {
    fetchData() {
      this.listLoading = true
      getAdminList(this.listQuery).then(res => {
        this.list = (res.data && res.data.list) || []
        this.total = (res.data && res.data.total) || 0
        this.listLoading = false
      }).catch(() => {
        this.listLoading = false
      })
    },
    fetchSchoolOptions() {
      getSchoolOptions().then(res => {
        this.schoolOptions = res.data || []
      }).catch(() => {
        this.schoolOptions = []
      })
    },
    handleAction(cmd, row) {
      if (cmd === 'reset') return this.handleResetPassword(row)
      if (cmd === 'delete') return this.handleDelete(row)
    },
    resetFilter() {
      this.listQuery.username = ''
      this.listQuery.role = ''
      this.handleFilter()
    },
    isMe(row) {
      return this.adminInfo && row.id === this.adminInfo.id
    },
    /** 超管账号不允许被其他人编辑 */
    canEdit(row) {
      if (this.isMe(row)) {
        return true
      }
      if (row.superAdmin) {
        return false
      }
      return this.isSuperAdmin || row.parentId === (this.adminInfo && this.adminInfo.id)
    },
    canResetPassword(row) {
      if (this.isMe(row) || row.superAdmin) {
        return false
      }
      return this.canEdit(row)
    },
    canDelete(row) {
      if (this.isMe(row) || row.superAdmin) {
        return false
      }
      return this.canEdit(row)
    },
    handleFilter() {
      this.listQuery.page = 1
      this.fetchData()
    },
    handleSizeChange(val) {
      this.listQuery.size = val
      this.fetchData()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchData()
    },
    handleRoleChange() {
      // 从管理员改成运营（或反过来）时，学校授权需要重新确认，避免误带过去
      if (this.form.role === 'super') {
        this.form.schoolIds = []
      }
    },
    selectAllSchools() {
      this.form.schoolIds = this.schoolOptions.map(item => item.id)
    },
    handleCreate() {
      this.resetForm()
      this.dialogTitle = '新增' + this.creatableRoleLabel
      this.isEdit = false
      this.isSelf = false
      this.roleLocked = false
      // 管理员只能建运营，此时没什么可选的，直接锁掉角色选择
      if (!this.isSuperAdmin) {
        this.form.role = 'operator'
        this.roleLocked = true
      }
      this.dialogVisible = true
    },
    handleUpdate(row) {
      this.resetForm()
      this.form = {
        id: row.id,
        username: row.username,
        nickname: row.nickname,
        password: '',
        role: row.role,
        schoolIds: [...(row.schoolIds || [])],
        status: row.status
      }
      this.dialogTitle = '编辑' + (row.roleLabel || '账号')
      this.isEdit = true
      this.isSelf = this.isMe(row)
      // 自己不能改自己的角色 —— 免得把唯一的超管降级后再也改不回来
      this.roleLocked = this.isSelf || row.superAdmin
      this.dialogVisible = true
    },
    handleDelete(row) {
      this.$confirm(`确定要删除「${row.nickname}」吗？该账号的学校授权会一并清除。`, '提示', {
        type: 'warning'
      }).then(() => {
        deleteAdmin(row.id).then(() => {
          this.$message.success('删除成功')
          this.fetchData()
        })
      }).catch(() => {})
    },
    handleResetPassword(row) {
      this.$confirm(`确定要重置「${row.nickname}」的密码吗？`, '提示', {
        type: 'warning'
      }).then(() => {
        resetAdminPassword(row.id).then(res => {
          this.$alert(`新密码：${res.data.newPassword}`, '重置成功', {
            confirmButtonText: '我已记下'
          })
        })
      }).catch(() => {})
    },
    submitForm() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return
        }
        if (this.form.role !== 'super' && (!this.form.schoolIds || !this.form.schoolIds.length)) {
          this.$confirm('该账号还没有授权任何学校，登录后将看不到数据。确定继续吗？', '提示', {
            type: 'warning'
          }).then(() => this.doSave()).catch(() => {})
          return
        }
        this.doSave()
      })
    },
    doSave() {
      this.submitting = true
      const payload = {
        nickname: this.form.nickname,
        role: this.form.role,
        status: this.form.status,
        schoolIds: this.form.role === 'super' ? [] : this.form.schoolIds
      }

      const request = this.isEdit
        ? updateAdmin({ ...payload, id: this.form.id })
        : createAdmin({ ...payload, username: this.form.username, password: this.form.password })

      request.then(() => {
        this.$message.success(this.isEdit ? '修改成功' : '创建成功')
        this.dialogVisible = false
        this.submitting = false
        this.fetchData()
      }).catch(() => {
        this.submitting = false
      })
    },
    resetForm() {
      this.form = {
        id: null,
        username: '',
        nickname: '',
        password: '',
        role: this.isSuperAdmin ? 'admin' : 'operator',
        schoolIds: [],
        status: 1
      }
    },
    formatDate(date) {
      if (!date) return ''
      return date
    }
  }
}
</script>

<style lang="scss" scoped>
/* 角色图例：一眼看清三级层级关系 */
.role-bar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.role-bar__item {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--ink-200);
  border-radius: var(--r-lg);
  padding: 12px 16px;
}

.role-bar__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.role-bar__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink-900);
}

.role-bar__desc {
  font-size: 12px;
  color: var(--ink-400);
}

.badge {
  flex: 0 0 auto;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}

.badge--super {
  background: var(--rose-500);
}

.badge--admin {
  background: var(--coral);
}

.badge--operator {
  background: var(--ink-300);
}

/* 角色胶囊 */
.role-chip {
  display: inline-block;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: 20px;
  line-height: 18px;
}

.role-chip--super {
  background: var(--rose-50);
  color: var(--rose-600);
  font-weight: 500;
}

.role-chip--admin {
  background: #fff1eb;
  color: #c2552f;
}

.role-chip--operator {
  background: var(--ink-100);
  color: var(--ink-500);
}

.scope-all {
  font-size: 13px;
  color: var(--rose-600);
}

.scope-none {
  font-size: 13px;
  color: var(--warn);
}

.school-chip {
  display: inline-block;
  font-size: 12px;
  background: var(--ink-100);
  color: var(--ink-500);
  border-radius: 5px;
  padding: 2px 8px;
  margin: 2px 4px 2px 0;
}

.school-more {
  font-size: 12px;
  color: var(--ink-400);
  cursor: pointer;
}

.no-action {
  font-size: 12px;
  color: var(--ink-300);
}

.field-hint {
  font-size: 12px;
  color: var(--ink-400);
  line-height: 1.6;
}

.role-select {
  width: 140px;
}
</style>
